<script setup lang="ts">
import { computed } from 'vue'
import { NButton } from 'naive-ui'
import type { SourceRow, SourceStatus } from '../stores/source_activity'
import { formatDate } from '../composables/useFormattedDate'
import { useNow } from '../composables/useNow'
import { useWikiMarkdown } from '../composables/useWikiMarkdown'
import CitationHoverManager from './CitationHoverManager.vue'

const props = defineProps<{
  row: SourceRow
  // Same queue-position/queue-depth props as the parent — passed straight
  // through so the Queued status line can read "Position 2 of 3".
  queuePosition?: number
  queueDepth?: number
}>()

const emit = defineEmits<{
  (e: 'open-path', path: string): void
}>()

// Query answers go through the same wiki-aware renderer as everything
// else so bare `entities/foo.md` paths in the body become SPA links and
// external URLs open in a new tab. When the result carries citations,
// hand them in so each cited path picks up a numbered `[n]` marker
// inline. `currentPath` defaults to `index.md` — answers aren't anchored
// to a specific page, so root-relative resolution is the right start.
const wikiMarkdown = useWikiMarkdown()
const answerHtml = computed(() => {
  const answer = props.row.result?.answer
  if (!answer) return ''
  const cites = props.row.result?.citations ?? []
  return cites.length > 0
    ? wikiMarkdown.renderWithCitations(answer, 'index.md', cites)
    : wikiMarkdown.render(answer, 'index.md')
})

const pagesTouched = computed(() => props.row.result?.pages_touched ?? [])
// What the ingest actually STORED — log.md is the always-appended
// activity log, not content the user authored, so it doesn't count as a
// "page written" and (when it's the only thing touched) signals a no-op.
const contentPages = computed(() => pagesTouched.value.filter((p) => p !== 'log.md'))
const citations = computed(() => props.row.result?.citations ?? [])

// No-op ingest: the run succeeded but stored no content page — the
// model judged there was nothing concrete to save. Requires `result` to
// be present (a succeeded row whose result hasn't loaded isn't a no-op)
// and excludes query answers (routing 'query' / a populated answer) and
// reminder commands, which never write pages.
const isNoOpIngest = computed(() => {
  const r = props.row.result
  return (
    props.row.status === 'succeeded' &&
    !!r &&
    r.routing !== 'query' &&
    !r.answer &&
    !r.reminder &&
    contentPages.value.length === 0
  )
})

// The confirmation is Discord-flavoured markdown (`**message**`), so it
// goes through the same renderer as answers.
const reminder = computed(() => props.row.result?.reminder ?? null)
const reminderHtml = computed(() => {
  const text = reminder.value?.confirmation
  return text ? wikiMarkdown.render(text, 'index.md') : ''
})

// Header label that introduces the full input in the expanded panel.
// Names the section ("Prompt", "File", "URL") so the user knows what
// they're looking at when an in-flight row is opened before any
// answer/result has streamed back.
const promptHeading = computed(() => {
  switch (props.row.kind) {
    case 'web_prompt':
      return 'Prompt'
    case 'file_upload':
      return 'File'
    case 'url':
      return 'URL'
    default:
      return 'Input'
  }
})

const promptBody = computed(() => {
  if (props.row.kind === 'url') return props.row.url ?? ''
  return props.row.content ?? ''
})

const hasPromptBody = computed(() => promptBody.value.trim().length > 0)

// The Source row carries one timestamp per status transition through
// the `updated_at` field (the model broadcasts on every status change).
// `created_at` therefore pins when the row was queued; `updated_at`
// pins the most recent transition — for running rows, that's when the
// worker picked it up.
const now = useNow()
const queuedAtLabel = computed(() => formatDate(props.row.created_at, now.value))
const lastChangeLabel = computed(() => formatDate(props.row.updated_at, now.value))

const isInFlightRow = computed(() => isInFlight(props.row.status))

function isInFlight(status: SourceStatus | null): boolean {
  return status === 'queued' || status === 'running'
}
</script>

<template>
  <div class="flex flex-col gap-3 pt-1 border-t border-white/5">
    <section v-if="hasPromptBody" class="flex flex-col gap-1" data-testid="activity-prompt">
      <h4 class="text-xs font-semibold uppercase tracking-widest text-white/40 m-0">
        {{ promptHeading }}
      </h4>
      <p class="text-sm text-white/80 whitespace-pre-wrap break-words m-0">{{ promptBody }}</p>
    </section>

    <section v-if="isInFlightRow" class="flex flex-col gap-1" data-testid="activity-status-detail">
      <h4 class="text-xs font-semibold uppercase tracking-widest text-white/40 m-0">Status</h4>
      <p class="text-sm text-white/60 m-0">
        <span v-if="row.status === 'queued'">
          Queued {{ queuedAtLabel }}, waiting for a worker.<template
            v-if="queuePosition && queueDepth && queueDepth > 1"
          >
            Position {{ queuePosition }} of {{ queueDepth }} in the queue.</template
          >
        </span>
        <span v-else-if="row.status === 'running'"
          >Running since {{ lastChangeLabel }}. Queued {{ queuedAtLabel }}.</span
        >
      </p>
    </section>

    <section v-if="row.error" class="text-sm text-red-300">{{ row.error }}</section>

    <!-- No-op ingest: explain why nothing was stored and how to
         retry, in the same flow — instead of a bare "Done". -->
    <section v-if="isNoOpIngest" class="flex flex-col gap-1" data-testid="activity-noop">
      <h4 class="text-xs font-semibold uppercase tracking-widest text-white/40 m-0">Nothing was stored</h4>
      <p v-if="row.result?.note" class="text-sm text-white/70 m-0">{{ row.result.note }}</p>
      <p class="text-sm text-white/50 m-0">
        The model didn't find a concrete fact to save. Try a specific statement (e.g. “Acme was founded in 2020”) rather
        than a question or a vague pointer.
      </p>
    </section>

    <section v-if="reminder" class="flex flex-col gap-1" data-testid="activity-reminder">
      <h4 class="text-xs font-semibold uppercase tracking-widest text-white/40 m-0">Reminder</h4>
      <p v-if="reminder.error" class="text-sm text-red-300 m-0">{{ reminder.error }}</p>
      <article v-else class="wiki-markdown text-sm" v-html="reminderHtml" />
    </section>

    <section v-if="contentPages.length" class="flex flex-col gap-1">
      <h4 class="text-xs font-semibold uppercase tracking-widest text-white/40 m-0">
        Pages written ({{ contentPages.length }})
      </h4>
      <ul class="list-none p-0 m-0 flex flex-col gap-1">
        <li v-for="p in contentPages" :key="p">
          <NButton text type="primary" class="!font-mono !text-xs" @click="emit('open-path', p)">{{ p }}</NButton>
        </li>
      </ul>
    </section>

    <section v-if="row.result?.answer" class="flex flex-col gap-1">
      <h4 class="text-xs font-semibold uppercase tracking-widest text-white/40 m-0">Answer</h4>
      <CitationHoverManager>
        <article class="wiki-markdown text-sm" v-html="answerHtml" />
      </CitationHoverManager>
    </section>

    <section v-if="citations.length" class="flex flex-col gap-1">
      <h4 class="text-xs font-semibold uppercase tracking-widest text-white/40 m-0">
        Citations ({{ citations.length }})
      </h4>
      <ul class="list-none p-0 m-0 flex flex-col gap-1">
        <li v-for="c in citations" :key="c.path">
          <NButton text type="primary" class="!font-mono !text-xs" @click="emit('open-path', c.path)">{{
            c.path
          }}</NButton>
        </li>
      </ul>
    </section>
  </div>
</template>
